<script setup>
import { computed, ref } from 'vue';
import useProductFinderStore from '../../stores/productFinder';
import { isExpanded, expandCollapse } from '../../utils/expandCollapse';
import CheckboxToggle from './CheckboxToggle';
import { default as t } from '../../translations/translationStrings';

// Data.
const trigger = ref(null);
const dropdown = ref(null);
// Track the local state of the dropdown.
// It is not possible to have a computed on a DOM element.
const isOpen = ref(false);

// Props.
const props = defineProps({
  label: {
    required: true,
    type: String,
  },
  labelEn: {
    type: String,
    default: '',
  },
  allLabel: {
    type: String,
    default: '',
  },
  icon: {
    type: String,
    default: '',
  },
  items: {
    required: true,
    type: Array,
  },
  parentId: {
    type: Number,
    default: null,
  },
  selectedItemNames: {
    type: Array,
    default: () => [],
  },
  buttonSelectText: {
    required: true,
    type: String,
  },
});

// Computed & store getters.
const { getMainCrops } = useProductFinderStore();
const mainCrops = computed(() => getMainCrops());

// Model value.
const modelValue = defineModel();

// Computed.
const filteredSelectedItems = computed(() => {
  if (modelValue.value.includes(props.parentId)) {
    return [
      { id: props.parentId, name: props.allLabel || `${t.all} ${props.label}` },
    ];
  }

  return props.items.filter((item) => modelValue.value.includes(item.id));
});

// Methods.
const toggleDropdown = () => {
  expandCollapse(dropdown.value, trigger.value);
  isOpen.value = isExpanded(dropdown.value);
};

const toggleAllModelValue = (values) => {
  if (mainCrops.value.some((crop) => values.includes(crop.id))) {
    modelValue.value = mainCrops.value
      .filter((crop) => values.includes(crop.id))
      .map((crop) => crop.id);
  } else {
    modelValue.value = values;
  }

  // modelValue.value = values.includes(props.parentId)
  //   ? [props.parentId]
  //   : values;
};

const toggleOptionsModelValue = (values) => {
  modelValue.value = values.filter((value) => value !== props.parentId);
};
</script>

<template>
  <div class="multi-select">
    <div
      ref="trigger"
      @click.prevent="toggleDropdown"
      class="multi-select__input">
      <div class="multi-select__text">
        <div
          class="multi-select__label"
          :class="{
            'multi-select__label--icon': icon,
            'multi-select__label--items-selected': selectedItemNames.length > 0,
          }">
          <!-- Icon (if any). -->
          <div
            v-if="icon"
            class="multi-select__icon-wrapper"
            :class="{
              'multi-select__icon-wrapper--highlighted':
                filteredSelectedItems.length > 0,
            }">
            <img :src="icon" :alt="label" class="multi-select__icon" />
          </div>

          <!-- Select name. -->
          {{ label }}
        </div>
        <!-- 'Chips': selected filter items. -->
        <Transition name="multi-select-selected">
          <div
            class="multi-select__selected"
            v-if="filteredSelectedItems.length && !isOpen">
            <div
              v-for="item in filteredSelectedItems"
              :key="`${item.name}-selected`"
              :title="item.name"
              class="multi-select__selected-item"
              @click.stop="
                modelValue = modelValue.filter((value) => value !== item.id)
              ">
              <span>{{ item.name }}</span>
              <i class="far fa-times"></i>
            </div>
          </div>
        </Transition>
      </div>
    </div>
    <div ref="dropdown" class="multi-select__dropdown">
      <ul class="multi-select__items">
        <!-- Select all. -->
        <li v-if="parentId !== null" class="multi-select__item">
          <CheckboxToggle
            :label="allLabel || `${t.all} ${label}`"
            :label-en="`All ${labelEn}`"
            :value="parentId"
            v-model="modelValue"
            @update:modelValue="toggleAllModelValue($event)" />
        </li>

        <!-- The rest of the options. -->
        <li v-for="item in items" class="multi-select__item" :key="item.id">
          <CheckboxToggle
            :label="item.name"
            :label-en="item.name_en"
            :value="item.id"
            v-model="modelValue"
            @update:modelValue="toggleOptionsModelValue($event)" />
        </li>
      </ul>
    </div>
  </div>
</template>
